import React, { useEffect, useRef, useState, useMemo } from "react";
import "./Places.scss";
import { Col, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useLocalization, getStatesForCountry } from '../../../contexts/LocalizationContext';
import * as validate from "../helper";
import mexico from "../../../constants/mx-states";
import AddressAutocomplete from "./AddressAutocomplete";
interface iState{ code: string; name: string; en?: string }
interface AddressFormState {
  pincode: string;
  address: string;
  line2: string;
  cityValue?: string;
  stateValue?: string;
  county?: string;
  countryName: string;
  countryValue: string;
  brand?: string;
}
export interface iAddessProps {
  isDownload: boolean;
  isVerify: boolean;
  isThanks: boolean;
  validationErrors: Record<string, string>;
  isBillingReadonly: boolean;
  billingAddr: any;
  addressOverride?: Partial<AddressFormState> | null;
  brand?: string; // Optional brand prop
  onPlaceUpdate: (
    data: AddressFormState,
  ) => void; // Callback for sending data to parent
  onFieldBlur?: (fieldName: string, value: string) => void;
}
const Places: React.FC<iAddessProps> = ({
  isThanks,
  isDownload,
  isVerify,
  validationErrors,
  isBillingReadonly,
  billingAddr,
  addressOverride,
  onPlaceUpdate,
  onFieldBlur,
  brand
}) => {
  const isReadonly = isBillingReadonly;

  const inputRef = useRef<HTMLInputElement | null>(null);
  const { t } = useTranslation();
  const { locale, stateDropdown, countryList, countryListPhysical, countyField, states } = useLocalization();

  const countries = useMemo(() => {
    return isDownload ? countryList : (countryListPhysical || countryList);
  }, [isDownload, countryList, countryListPhysical])

  const [selectedCountryCode, setSelectedCountryCode] = useState(countries[0].ISO_CC);

  const allowedStates = useMemo(() => {
    if (brand === 'MSI' && locale === 'en-us') {
      return states?.filter((state:iState) => state.code !== 'AK' && state.code !== 'HI');
    }
    if (locale === 'en-af' || locale === 'es-la') {
      return getStatesForCountry(selectedCountryCode);
    }
    return states;
  }, [brand, locale, states, selectedCountryCode]);
  const [addressState, setAddressState] = useState<AddressFormState>({
    pincode: "",
    address: "",
    line2: "",
    cityValue: locale === 'en-sg' ? "Singapore" : "",
    ...((stateDropdown || locale === 'zh-tw') && { stateValue: locale === 'zh-tw' ? "TW" : "" }),
    ...(countyField && { county: "" }),
    countryName: locale !== 'en-ap' ? countries[0].countryName : '',
    countryValue: locale !== 'en-ap' ? countries[0].ISO_CC : 'CN',
  });

  useEffect(() => {
    if (!addressOverride) {
      return;
    }

    if (addressOverride.countryValue) {
      setSelectedCountryCode(addressOverride.countryValue);
    }
    setAddressState((currentAddress) => {
      const syncedAddress: AddressFormState = {
        ...currentAddress,
        pincode: addressOverride.pincode ?? "",
        address: addressOverride.address ?? "",
        line2: addressOverride.line2 ?? "",
        cityValue: addressOverride.cityValue ?? "",
        stateValue: addressOverride.stateValue ?? "",
        county: addressOverride.county ?? "",
        countryName:
          addressOverride.countryName ?? currentAddress.countryName,
        countryValue:
          addressOverride.countryValue ?? currentAddress.countryValue,
      };

      const isAlreadySynced = (
        Object.keys(syncedAddress) as Array<keyof AddressFormState>
      ).every((key) => syncedAddress[key] === currentAddress[key]);

      return isAlreadySynced ? currentAddress : syncedAddress;
    });
  }, [addressOverride]);


  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.setAttribute("autoComplete", "none");
    }
  });

  const handlePlaceSelect = (place: any) => {
    const addressComponents = place.address_components;

    let newPincode = (locale === 'en-hk' || locale === 'en-me' || (locale === 'es-la' && addressState['countryValue'] === 'PA')) ? "00000" : addressState.pincode || "";
    let newAddress = "";
    let geoStateValue = locale === 'zh-tw' ? "TW" : addressState.stateValue || "";
    let geoCityValue = locale === 'en-sg' ? "Singapore" : addressState.cityValue || "";
    let geoCountyValue = addressState.county || "";
    let geoCountryValue = locale !== 'en-ap' ? addressState.countryValue : 'CN';
    let geoCountryName = addressState.countryName;

    if (addressComponents) {
      // Handle Google Places autocomplete selection
      let streetNumber = "";
      let route = "";
      let neighborhood = "";
      let sublocality = "";
      let indiaPlusCode = "";
      let indiaPremise = "";
      let indiaSublocality1 = "";
      let indiaSublocality2 = "";

      for (const component of addressComponents) {
        if (component.types.includes("street_number")) {
          streetNumber = component.long_name;
        }
        if (component.types.includes("route")) {
          route = component.long_name;
        }
        if (locale === "en-in") {
          if (component.types.includes("plus_code")) {
            indiaPlusCode = component.long_name;
          }
          if (component.types.includes("premise")) {
            indiaPremise = component.long_name;
          }
          if (component.types.includes("sublocality_level_1")) {
            indiaSublocality1 = component.long_name;
          }
          if (component.types.includes("sublocality_level_2")) {
            indiaSublocality2 = component.long_name;
          }
        }
        if (component.types.includes("neighborhood")) {
          neighborhood = (newAddress ? ", " : "") + component.long_name;
        }
        if (component.types.includes("sublocality") || component.types.includes("sublocality_level_1")) {
          if (locale === 'en-ph') {
            // Barangay mapping
            neighborhood = component.long_name;
          } else {
            sublocality = (newAddress ? ", " : "") + component.long_name;
          }
        }
        if (selectedCountryCode.toLowerCase() !== 'us' && component.types.includes("postal_town")) {
          geoCityValue = component.long_name;
        }
        if (component.types.includes("locality")) {
          geoCityValue = component.long_name;
        }
        if (component.types.includes("postal_code")) {
          newPincode = component.long_name; // rare for HK
        }
        if (component.types.includes("administrative_area_level_1")) {
          if (locale === 'zh-tw' || locale === 'ko-kr') {
            geoCityValue = component.long_name;
          } else {
            // ✅ United States or default case using dropdown validation
            if (stateDropdown) {
              const stateCode = component.short_name;
              // Verify if the state code exists in geoStates
              const validState = allowedStates?.find(state => state.code === stateCode);
              if (validState) {
                geoStateValue = stateCode
              }
            }
            if (locale === 'zh-tw') {
              geoStateValue = 'TW'
            }
            if (locale === 'en-ph' && component.short_name === "NCR") {
              geoStateValue = component.short_name; // Province short code (PH)
            }
            // ✅ Special handling for Mexican states
            if (locale === 'es-mx' || component.short_name.endsWith('.')) {
              const normalized = mexico.find(mx =>
                mx.name.toLowerCase().replace(/\s|\(.*?\)/g, '') === component.long_name.toLowerCase().replace(/\s|\(.*?\)/g, '')
              );
              if (normalized) {
                geoStateValue = normalized.code;
              }
            }
          }
        }
        // ✅ Special handling for Brazilian cities
        if (component.types.includes("administrative_area_level_2") && (locale === 'en-me' || locale === 'pt-br' || locale === 'en-ph' || locale === 'en-af')) {
          if (locale === 'en-ph' || locale === 'en-af') {
            geoStateValue = component.short_name; // Province short code (PH)
          } else {
            geoCityValue = component.long_name;
          }
        }
        if (component.types.includes("country")) {
          const countryCode = component.short_name.toUpperCase();
          const validCountry = countries.find(country => country.ISO_CC === countryCode);
          if (validCountry && locale !== 'en-ap') {
            geoCountryValue = countryCode;
            setSelectedCountryCode(countryCode);
            geoCountryName = validCountry?.countryName || "";
          }
        }
        if (component.types.includes("postal_code")) {
          newPincode = component.long_name; // rare for HK
        }
        // 🔹 Hong Kong-specific handling
        if (geoCountryValue === "HK") {
          // Highest priority: neighborhood, then sublocality, then locality
          if (component.types.includes("neighborhood")) {
            geoCityValue = component.long_name; // e.g., "Soho"
          } else if (component.types.includes("sublocality_level_1")) {
            geoCityValue = component.long_name; // e.g., "Tsim Sha Tsui"
          } else if (component.types.includes("locality")) {
            geoCityValue = component.long_name; // e.g., "Kowloon"
          }

          if (locale === 'en-hk' || locale === 'en-me' || (locale === 'es-la' && addressState['countryValue'] === 'PA')) {
            newPincode = "00000";
          }
        }
      }
      // Combine street number and route to create the address
      if (streetNumber || route) {
        newAddress = streetNumber + " " + route;
      } else if (streetNumber) {
        newAddress = streetNumber;
      } else if (route) {
        newAddress = route;
      }

      if (locale === "en-in") {
        const streetLine = [streetNumber, route].filter(Boolean).join(" ").trim();
        const indiaParts = [
          indiaPlusCode,
          indiaPremise,
          streetLine,
          indiaSublocality1,
          indiaSublocality2,
        ].filter(Boolean);
        const seen = new Set<string>();
        newAddress = indiaParts
          .filter((p) => {
            if (seen.has(p)) return false;
            seen.add(p);
            return true;
          })
          .join(", ");
      }

      // Append neighborhood and sublocality for Middle East locale when both exist
      if (locale === 'en-me' && neighborhood && sublocality) {
        newAddress = (newAddress ? newAddress + ", " : "") + `${neighborhood}, ${sublocality}`;
      }

      if (!(newAddress?.length > 0) || !(validate.validateStringOrIntegerAddress(newAddress, locale))) {
        newAddress = place.formatted_address.split(",")[0] || "";
        if (locale === 'ja-jp') {
          const parts = place.formatted_address.split(",");
          if (parts[0].indexOf("Japan") > -1) {
            newAddress = parts[parts.length - 2] + parts[parts.length - 1];
          } else {
            newAddress = parts[0] + parts[1];
          }
        } else if (locale !== 'es-es' && locale !== 'it-it' && locale !== 'en-gb') {
          newAddress = place.formatted_address.split(",")[0] || "";
        } else {
          newAddress = place.formatted_address.split(",")[0] + place.formatted_address.split(",")[1];
        }
      }
    } else if (place.target) {
      // Handle form field changes
      newPincode = locale === 'en-hk' || locale === 'en-me' || (locale === 'es-la' && addressState['countryValue'] === 'PA') ? "00000" : addressState.pincode || place.target.value || "";
      newAddress = addressState.address || place.target.value || "";
      geoCityValue = locale === 'en-sg' ? "Singapore" : addressState.cityValue || "";
      geoStateValue = locale === 'zh-tw' ? "TW" : addressState.stateValue || "";
      geoCountyValue = addressState.county || place.target.value || "";
      const { name, value } = place.target;
      // Reset location-specific fields when country changes
      newPincode = locale === 'en-hk' || locale === 'en-me' || (locale === 'es-la' && addressState['countryValue'] === 'PA') ? "00000" : addressState.pincode || "";
      newAddress = addressState.address || "";
      if (name === 'addressBilling' || name === 'addressShipping') {
        newAddress = value || "";
      }
      geoCityValue = locale === 'en-sg' ? "Singapore" : addressState.cityValue || "";
      if (name === 'cityBilling' || name === 'cityShipping') {
        geoCityValue = value || "";
      }
      geoStateValue = locale === 'zh-tw' ? "TW" : addressState.stateValue || "";
      if (name === 'stateNameBilling' || name === 'stateNameShipping') {
        geoStateValue = value || "";
      }
      geoCountyValue = addressState.county || place.target.value || "";
      if (name === 'countyBilling' || name === 'countyShipping') {
        geoCountyValue = value || "";
      }
      geoCountryValue = locale !== 'en-ap'
        ? (addressState.countryValue || selectedCountryCode || "")
        : 'CN';
      if (name === 'countryValueBilling' || name === 'countryValueShipping') {
        geoCountryValue = value || "";
      }
      geoCountryName = locale !== 'en-ap'
        ? (addressState.countryName || countries.find(country => country.ISO_CC === (addressState.countryValue || selectedCountryCode || ""))?.countryName || "")
        : (addressState.countryName || "");
      if (name === 'countryValueBilling' || name === 'countryValueShipping') {
        geoCountryValue = value || "";
        geoCountryName = locale !== 'en-ap' ? countries.find(country => country.ISO_CC === value)?.countryName || "" : value || "";
      }
      if (name === 'countryValueBilling') {
        // Handle country dropdown change
        const selectedCountry = countries.find(country => country.ISO_CC === value);
        if (selectedCountry) {
          geoCountryValue = locale !== 'en-ap' ? selectedCountry.ISO_CC : value || '';
          geoCountryName = locale !== 'en-ap' ? selectedCountry.countryName : value || '';

          if (locale === 'en-eu' || locale === 'en-af' || locale === 'de-de' || locale === 'es-la' || locale === 'en-gb') {
            // Reset location-specific fields when country changes
            newPincode = "";
            newAddress = "";
            geoCityValue = "";
            geoStateValue = "";
            geoCountyValue = "";
          }

          // Update selected country code for Places API
          setSelectedCountryCode(value === 'ME' ? 'AE' : value);

          // Reset Google Places input
          if (inputRef.current) {
            inputRef.current.value = '';
          }
        }
      } else if (name === `addressBilling`) {
        newAddress = value.split(",")[0];
      } else if (name === `cityBilling`) {
        geoCityValue = value;
      } else if (name === `stateNameBilling`) {
        geoStateValue = locale === 'zh-tw' ? "TW" : value;
      } else if (name === `countyBilling`) {
        geoCountyValue = value;
      } else if (name === `zipBilling`) {
        newPincode = value;
        if (locale === 'en-hk' || locale === 'en-me' || (locale === 'es-la' && addressState['countryValue'] === 'PA')) {
          newPincode = "00000";
        }
      }
    }

    if (locale === 'en-ap') {
      geoCountryValue = 'CN';
    }

    // Update address state
    const newAddressState = {
      ...addressState,
      pincode: newPincode,
      address: newAddress,
      cityValue: geoCityValue,
      ...((locale === 'zh-tw' || stateDropdown || locale === 'en-ap') && { stateValue: locale === 'zh-tw' ? "TW" : geoStateValue }),
      ...(countyField && { county: geoCountyValue }),
      countryName: geoCountryName,
      countryValue: geoCountryValue,
    };

    setAddressState(newAddressState);
  };

  const handlePlaceBlur = (event: React.FocusEvent<HTMLElement>) => {
    handlePlaceSelect(event);
    const target = event.target as HTMLInputElement | HTMLSelectElement;
    const fieldMap: Record<string, string> = {
      addressBilling: "address",
      line2Billing: "line2",
      cityBilling: "cityValue",
      stateNameBilling: "stateValue",
      zipBilling: "pincode",
      countryValueBilling: "countryValue",
    };
    const fieldName = fieldMap[target.name];
    if (fieldName) {
      onFieldBlur?.(fieldName, target.value);
    }
  };

  useEffect(() => {
    let addrData: any = {
      ...{ pincode: locale === 'en-hk' || locale === 'en-me' || (locale === 'es-la' && addressState['countryValue'] === 'PA') ? "00000" : addressState.pincode },
      ...{ address: addressState.address },
      ...{ line2: addressState.line2 },
      ...{ cityValue: addressState.cityValue },
      ...((stateDropdown || locale === 'zh-tw' || locale === 'en-ap') && { stateValue: locale === 'zh-tw' ? "TW" : addressState.stateValue }),
      ...(countyField && { county: addressState.county }),
      ...{ countryName: addressState.countryName },
      ...{ countryValue: locale === 'en-ap' ? 'CN' : addressState.countryValue },
    }

    if (!addressState.pincode && !addressState.address && !addressState.cityValue && !addressState.stateValue) {
      addrData = {}
    };
    if (Object.keys(addrData).length > 0) {
      onPlaceUpdate(addrData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressState]);
  useEffect(() => {
    if (billingAddr) {
      const {
        postalCode,
        line1,
        city,
        countrySubdivision,
        county,
        countryName,
        country,
        line2,
      } = JSON.parse(billingAddr);
      setAddressState({
        ...addressState,
        pincode: locale === 'en-hk' || locale === 'en-me' || (locale === 'es-la' && addressState['countryValue'] === 'PA') ? "00000" : postalCode,
        address: line1,
        line2: line2,
        cityValue: locale === 'en-sg' ? "Singapore" : city,
        stateValue: locale === 'zh-tw' ? "TW" : countrySubdivision || '',
        county: county || '',
        countryName: countryName,
        countryValue: locale === 'en-ap' ? 'CN' : country,
      });

      onPlaceUpdate(
        {
          pincode: locale === 'en-hk' || locale === 'en-me' || (locale === 'es-la' && addressState['countryValue'] === 'PA') ? "00000" : postalCode,
          address: line1,
          line2: line2,
          cityValue: locale === 'en-sg' ? "Singapore" : city,
          stateValue: locale === 'zh-tw' ? "TW" : countrySubdivision || '',
          county: county || '',
          countryName: countryName,
          countryValue: locale === 'en-ap' ? 'CN' : country,
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billingAddr]);
  return (
    <div className={`Places billing`}>
      <Col md={6}><Form.Group controlId={`addressBilling`}>
        <Form.Label className="visually-hidden" htmlFor={`addressBilling`}>{t("ADDRESS1_COLON")}</Form.Label>
        {locale === 'vi-vn' || locale === 'en-ap' ? (
          <Form.Control
            id={`addressBilling`}
            placeholder={t("ADDRESS1_REQUIRED")}
            className={`form-control ${validationErrors[`address`] ? "is-invalid" : ""}  ${isReadonly ? "disabled" : ""} `}
            type="text"
            autoComplete="none"
            name={`addressBilling`}
            readOnly={isReadonly}
            value={addressState.address}
            isInvalid={!!validationErrors[`address`]}
            onChange={handlePlaceSelect}
            onBlur={handlePlaceBlur}
            aria-required="true"
            aria-invalid={!!validationErrors[`address`]}
            aria-describedby={validationErrors[`address`] ? `addressBillingError` : undefined}
          />
        ) : (
          <AddressAutocomplete
            id={`addressBilling`}
            name={`addressBilling`}
            className={`form-control ${validationErrors[`address`] ? "is-invalid" : ""}  ${isReadonly ? "disabled" : ""} `}
            inputRef={inputRef}
            placeholder={t("ADDRESS1_REQUIRED")}
            countryCode={selectedCountryCode}
            onPlaceSelected={handlePlaceSelect}
            onBlur={handlePlaceBlur}
            onChange={handlePlaceSelect}
            value={addressState.address}
            readOnly={isReadonly}
            disabled={isReadonly}
            isInvalid={!!validationErrors[`address`]}
            ariaRequired
            ariaDescribedBy={validationErrors[`address`] ? `addressBillingError` : undefined}
          />
        )}
        {validationErrors[`address`] && (
          <Form.Control.Feedback type="invalid" id={`addressBillingError`} aria-live="polite">
            {validationErrors[`address`]}
          </Form.Control.Feedback>
        )}
      </Form.Group>
      </Col>
      <Col md={6}><Form.Group controlId={`line2Billing`}>
        <Form.Label className="visually-hidden" htmlFor={`line2Billing`}>{t("LINE2_OPTIONAL_COLON")}</Form.Label>
        <Form.Control
          id={`line2Billing`}
          type="text"
          autoComplete="none"
          readOnly={isReadonly}
          placeholder={t("LINE2_OPTIONAL")}
          className={`form-control  ${isReadonly ? "disabled" : ""} `}
          name={`line2Billing`}
          value={addressState.line2}
          onChange={(event: any) => {
            setAddressState({
              ...addressState,
              line2: event.target.value,
            })
          }}
        />
      </Form.Group>
      </Col>
      {locale !== 'en-sg' && (<Col md={6}><Form.Group controlId={`cityBilling`}>
        <Form.Label className="visually-hidden" htmlFor={`cityBilling`}>{t("CITY_COLON")}</Form.Label>
        <Form.Control
          id={`cityBilling`}
          placeholder={locale === 'en-sg' ? t("CITY") : t("CITY_REQUIRED")}
          type="text"
          autoComplete="none"
          readOnly={isReadonly}
          className={`form-control  ${isReadonly ? "disabled" : ""} `}
          name={`cityBilling`}
          value={addressState.cityValue}
          isInvalid={!!(validationErrors[`billingcityValue`] || validationErrors[`cityValue`])}
          onChange={handlePlaceSelect}
          onBlur={handlePlaceBlur}
          aria-required="true"
          aria-invalid={!!(validationErrors[`billingcityValue`] || validationErrors[`cityValue`])}
          aria-describedby={(validationErrors[`billingcityValue`] || validationErrors[`cityValue`]) ? `cityBillingError` : undefined}
        />
        {(validationErrors[`billingcityValue`] || validationErrors[`cityValue`]) && (
          <Form.Control.Feedback type="invalid" id={`cityBillingError`} aria-live="polite">
            {validationErrors[`billingcityValue`] || validationErrors[`cityValue`]}
          </Form.Control.Feedback>
        )}
      </Form.Group>
      </Col>)}

      {(stateDropdown || locale === 'en-ap') && (
        <Col md={6}>
          <Form.Group controlId={`stateNameBilling`}>
            <Form.Label className="visually-hidden" htmlFor={`stateNameBilling`}>{t("STATE")}</Form.Label>
            {locale === 'en-ap' ? (
              <Form.Control
                id={`stateNameBilling`}
                placeholder={t("STATE_REQUIRED")}
                type="text"
                autoComplete="none"
                name={`stateNameBilling`}
                value={addressState.stateValue}
                disabled={isReadonly}
                className={`form-control  ${isReadonly ? "disabled" : ""} `}
                onChange={handlePlaceSelect}
                onBlur={handlePlaceBlur}
                isInvalid={!!(validationErrors[`billingstateValue`] || validationErrors['stateValue'])}
                aria-required="true"
                aria-invalid={!!(validationErrors[`billingstateValue`] || validationErrors['stateValue'])}
                aria-describedby={validationErrors[`billingstateValue`]||validationErrors['stateValue'] ? `stateNameBillingError` : undefined}
              />
            ) : (
              <Form.Select
                id={`stateNameBilling`}
                autoComplete="none"
                value={addressState.stateValue}
                disabled={isReadonly}
                className={`form-control  ${isReadonly ? "disabled" : ""} `}
                name={`stateNameBilling`}
                onChange={handlePlaceSelect}
                onBlur={handlePlaceBlur}
                isInvalid={!!(validationErrors[`billingstateValue`] || validationErrors['stateValue'])}
                aria-required="true"
                aria-invalid={!!(validationErrors[`billingstateValue`] || validationErrors['stateValue'])}
                aria-describedby={validationErrors[`billingstateValue`]||validationErrors['stateValue'] ? `stateNameBillingError` : undefined}
              >
                <option value="">{t("SELECT_STATE")}</option>
                {allowedStates?.map((geoState, index) => (
                  <option key={index} value={geoState.code}>
                    {geoState.name}
                  </option>
                ))}
              </Form.Select>)}
            {(validationErrors[`billingstateValue`] || validationErrors['stateValue']) && (
              <Form.Control.Feedback type="invalid" id={`stateNameBillingError`} aria-live="polite">
                {validationErrors[`billingstateValue`]||validationErrors['stateValue']}
              </Form.Control.Feedback>
            )}
          </Form.Group>
        </Col>)}
      {locale !== 'en-hk' && locale !== 'en-me' && (<Col md={6}>
        <Form.Group controlId={`zipBilling`}>
          <Form.Label className="visually-hidden" htmlFor={`zipBilling`}>{t("ZIP_CODE_COLON")}</Form.Label>
          <Form.Control
            id={`zipBilling`}
            placeholder={t("ZIP_CODE_REQUIRED")}
            type="text"
            autoComplete="none"
            readOnly={isReadonly}
            className={`form-control  ${isReadonly ? "disabled" : ""} `}
            name={`zipBilling`}
            value={addressState.pincode}
            isInvalid={!!(validationErrors[`billingpincode`]||validationErrors['pincode'])}
            onChange={handlePlaceSelect}
            onBlur={handlePlaceBlur}
            aria-required="true"
            aria-invalid={!!(validationErrors[`billingpincode`]||validationErrors['pincode'])}
            aria-describedby={(validationErrors[`billingpincode`]||validationErrors['pincode']) ? `zipBillingError` : undefined}
          />
          {(validationErrors[`billingpincode`]||validationErrors['pincode']) && (
            <Form.Control.Feedback type="invalid" id={`zipBillingError`} aria-live="polite">
              {validationErrors[`billingpincode`]||validationErrors['pincode']}
            </Form.Control.Feedback>
          )}
        </Form.Group>
      </Col>)}
      <Col md={6}>
        <Form.Group controlId={`countryValueBilling`}>
          <div className={`${countries.length === 1 ? "singlecountry" : ""}`}>
            <Form.Label className="visually-hidden" htmlFor={`countryValueBilling`}>{t("COUNTRY_REQUIRED")}</Form.Label>
            {locale === 'en-ap' ? (
              <Form.Control
                id={`countryValueBilling`}
                name={`countryValueBilling`}
                placeholder={t("COUNTRY_REQUIRED")}
                type="text"
                autoComplete="none"
                value={addressState.countryName}
                disabled={isReadonly}
                className={`form-control  ${isReadonly ? "disabled" : ""} `}
                onChange={handlePlaceSelect}
                onBlur={handlePlaceBlur}
                isInvalid={!!validationErrors[`billingcountryValue`]}
                aria-required="true"
                aria-invalid={!!validationErrors[`billingcountryValue`]}
                aria-describedby={validationErrors[`billingcountryValue`] ? `countryValueBillingError` : undefined}
              />
            ) : (
              <Form.Select
                id={`countryValueBilling`}
                autoComplete="none"
                className={`form-control  ${isReadonly || countries.length === 1 ? "disabled" : ""} `}
                name={`countryValueBilling`}
                disabled={isReadonly || countries.length === 1}
                value={addressState.countryValue}
                onChange={handlePlaceSelect}
                onBlur={handlePlaceBlur}
                isInvalid={!!validationErrors[`billingcountryValue`]}
                aria-required="true"
                aria-invalid={!!validationErrors[`billingcountryValue`]}
                aria-describedby={validationErrors[`billingcountryValue`] ? `countryValueBillingError` : undefined}
              >

                {countries.map((country) => (
                  <option key={country.ISO_CC} value={country.ISO_CC}>
                    {country.countryName}
                  </option>
                ))}

              </Form.Select>)}
          </div>
          {validationErrors[`billingcountryValue`] && (
            <Form.Control.Feedback type="invalid" id={`countryValueBillingError`} aria-live="polite">
              {validationErrors[`billingcountryValue`]}
            </Form.Control.Feedback>
          )}
        </Form.Group>
      </Col>
    </div>
  );
};
export default Places;